import React, { useState, useEffect, useRef } from "react";
import {
  TextField,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  MenuItem,
  DialogTitle,
  Box,
  Stack,
  FormControlLabel,
  Typography,
  Checkbox,
  Alert,
} from "@mui/material";
import addStore from "../../../api/store/addStore";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { CITIES } from "../../../data/CITIES";
import getTags from "../../../api/store/getTags";
import updateStore from "../../../api/store/updateStore";
import { isLoveCodeValid } from "../../../invoiceUtils";

const storeStatus = {
  ClosedPermanently: "closed_permanently",
  Open: "open",
  Close: "close",
};

const defaultStore = {
  Code: "",
  ImaoStoreCode: "",
  Name: "",
  City: "",
  District: "",
  Address: "",
  Phone: "",
  Tables: 6,
  Tags: [],
  Status: storeStatus.Open,
  HideFromWeb: false,
  Description: "",
  IntervalMinutes: 30,
  Promotions: [
    {
      QuantityThreshold: 0,
      AmountThreshold: 200,
      RewardType: "Point",
      RewardValue: 1,
    },
  ],
  Latitude: 0.0,
  Longitude: 0.0,
  InvoiceEnable: false,
  InvoiceLoveCodes: [],
  InvoiceLoveCodeNames: {},
};

const AddStoreDialog = ({ isAdd, store, open, setOpen, onSaved }) => {
  const [storeData, setStoreData] = useState(defaultStore);
  const [selectedCity, setSelectedCity] = useState("");
  const [tags, setTagsState] = useState([]);
  const [loveCodeRows, setLoveCodeRows] = useState([]);
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);
  const nextLoveCodeId = useRef(0);

  const loveCodeErrors = loveCodeRows.map((row) => {
    const code = row.code.trim();
    const canKeepUnnamed =
      row.originalCode === code && row.originalName === "";
    return {
      name: !row.name.trim() && !canKeepUnnamed,
      code: !isLoveCodeValid(code)
        ? "請輸入 3 至 7 位數的愛心碼"
        : loveCodeRows.some(
            (other) => other.id !== row.id && other.code.trim() === code
          )
        ? "愛心碼不可重複"
        : "",
      canKeepUnnamed,
    };
  });
  const invoiceSettingsInvalid =
    (storeData.InvoiceEnable && !storeData.ImaoStoreCode?.trim()) ||
    loveCodeErrors.some((error) => error.name || error.code);

  const updateLoveCodeRow = (id, field, value) => {
    setLoveCodeRows((previous) =>
      previous.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
  };

  useEffect(() => {
    setSaveError("");
    const fetchTags = async () => {
      try {
        const data = await getTags();
        setTagsState(data.tags); // Adjust this if data structure differs
      } catch (err) {}
    };

    fetchTags();
    const codes = !isAdd && store ? store.InvoiceLoveCodes || [] : [];
    setLoveCodeRows(
      codes.map((code, id) => {
        const name = store.InvoiceLoveCodeNames?.[code] || "";
        return {
          id,
          code,
          name,
          originalCode: code.trim(),
          originalName: name.trim(),
        };
      })
    );
    nextLoveCodeId.current = codes.length;
    if (!isAdd && store) {
      // Ensure Tags is initialized if not present in the store object
      const updatedStore = {
        ...store,
        Tags: store.Tags || [],
        InvoiceEnable: Boolean(store.InvoiceEnable),
        InvoiceLoveCodes: store.InvoiceLoveCodes || [],
        InvoiceLoveCodeNames: store.InvoiceLoveCodeNames || {},
        HideFromWeb: Boolean(store.HideFromWeb),
      };

      setStoreData(updatedStore);
    } else {
      setStoreData(defaultStore);
    }
  }, [isAdd, store, open]);

  const StoreStatusSelect = () => {
    return (
      <>
        <Select
          name="Status"
          color="info"
          fullWidth
          value={storeData.Status || ""}
          onChange={handleChange}
          label="狀態"
        >
          <MenuItem color="info" value={storeStatus.Open}>
            營業
          </MenuItem>
          <MenuItem color="info" value={storeStatus.Close}>
            關店
          </MenuItem>
          <MenuItem color="info" value={storeStatus.ClosedPermanently}>
            永久停業
          </MenuItem>
        </Select>
      </>
    );
  };

  const IntervalMinutesSelect = () => {
    return (
      <>
        <Select
          name="IntervalMinutes"
          color="info"
          fullWidth
          value={storeData.IntervalMinutes || 30}
          onChange={handleChange}
          label="單位分鐘"
        >
          <MenuItem color="info" value={30}>
            30
          </MenuItem>
          <MenuItem color="info" value={60}>
            60
          </MenuItem>
        </Select>
      </>
    );
  };

  const handleClose = () => {
    setOpen(false);
  };

  const generatePromotionFields = () => {
    return storeData.Promotions?.map((promotion, index) => (
      <div key={index}>
        {promotion.RewardType === "Quantity" && (
          <>
            <Typography>時數促銷 #{index + 1}</Typography>
            <Stack direction={"row"} spacing={1} marginTop={1}>
              <TextField
                name={`${index}.QuantityThreshold`}
                label="滿時數"
                disabled={true}
                color="secondary"
                variant="outlined"
                value={storeData.Promotions[index].QuantityThreshold || 0}
                onChange={handlePromotionChange}
              />
              <TextField
                name={`${index}.RewardValue`}
                label="送"
                disabled={true}
                color="secondary"
                variant="outlined"
                value={storeData.Promotions[index].RewardValue || 0}
                onChange={handlePromotionChange}
              />
            </Stack>
          </>
        )}
        {promotion.RewardType === "Point" && (
          <>
            {" "}
            <Typography>點數促銷 #{index + 1}</Typography>
            <Stack direction={"row"} spacing={1} marginTop={1}>
              <TextField
                name={`${index}.AmountThreshold`}
                label="滿金額"
                color="secondary"
                variant="outlined"
                value={storeData.Promotions[index].AmountThreshold || 0}
                onChange={handlePromotionChange}
              />
              <TextField
                name={`${index}.RewardValue`}
                label="送"
                color="secondary"
                variant="outlined"
                value={storeData.Promotions[index].RewardValue || 0}
                onChange={handlePromotionChange}
              />
            </Stack>
          </>
        )}
      </div>
    ));
  };

  const handlePromotionChange = (event) => {
    const { name, value } = event.target;

    // Split the name to extract promotion index and property name
    const [promotionIndex, propertyName] = name.split(".");

    setStoreData((prevStoreData) => {
      // Make a deep copy of the previous store data
      const newStoreData = JSON.parse(JSON.stringify(prevStoreData));

      // Update the specific promotion property, ensuring immutability
      if (
        newStoreData.Promotions &&
        promotionIndex < newStoreData.Promotions.length
      ) {
        newStoreData.Promotions[promotionIndex] = {
          ...newStoreData.Promotions[promotionIndex],
          [propertyName]: Number(value),
        };
      }

      return newStoreData;
    });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setStoreData({
      ...storeData,
      [name]: name === "Latitude" || name === "Longitude" ? parseFloat(value) : value,
    });
  };

  const handleTagChange = (event) => {
    const { name, checked } = event.target;
    const newTags = checked
      ? [...storeData.Tags, name]
      : storeData.Tags.filter((tag) => tag !== name);

    setStoreData((prevStoreData) => ({
      ...prevStoreData,
      Tags: newTags,
    }));
  };

  const generateTagToggles = () => (
    <>
      <Typography marginRight={1} color={"error"}>
        注意:標籤僅供顯示給客人，內容不會影響任何優惠。
      </Typography>
      <Box direction="row" alignItems={"center"}>
        <Typography marginRight={1} variant="h6">
          標籤
        </Typography>
        {tags?.map((tag, index) => (
          <FormControlLabel
            key={index}
            control={
              <Checkbox
                color="warning"
                checked={storeData.Tags?.includes(tag)}
                onChange={handleTagChange}
                name={tag}
              />
            }
            label={tag}
          />
        ))}
      </Box>
    </>
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (invoiceSettingsInvalid || saving) {
      return;
    }
    const submittedStore = {
      ...storeData,
      InvoiceLoveCodes: loveCodeRows.map((row) => row.code.trim()),
      InvoiceLoveCodeNames: Object.fromEntries(
        loveCodeRows
          .filter((row) => row.name.trim())
          .map((row) => [row.code.trim(), row.name.trim()])
      ),
    };
    setSaveError("");
    setSaving(true);
    try {
      const response = isAdd
        ? await addStore(submittedStore)
        : await updateStore(storeData.Code, submittedStore);
      if (response.code && response.code !== "000") {
        throw new Error(response.message || "店家設定儲存失敗，請重試。");
      }
      await onSaved?.();
      handleClose();
    } catch (error) {
      setSaveError(
        error.response?.data?.message || error.message || "店家設定儲存失敗，請重試。"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog maxWidth={"lg"} open={open} onClose={handleClose}>
      <DialogTitle align="center">
        {isAdd ? "新增店家" : "編輯店家"}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
          <Stack spacing={5} direction={"row"}>
            <Box width={"50%"} flexDirection={"colunm"}>
              <TextField
                autoFocus
                margin="dense"
                name="Code"
                label="店號"
                color="secondary"
                required={true}
                variant="outlined"
                fullWidth
                value={storeData.Code || ""}
                onChange={handleChange}
              />
              <TextField
                margin="dense"
                name="ImaoStoreCode"
                label="發票系統店號"
                color="secondary"
                variant="outlined"
                fullWidth
                value={storeData.ImaoStoreCode || ""}
                onChange={handleChange}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    color="warning"
                    checked={Boolean(storeData.InvoiceEnable)}
                    onChange={(event) =>
                      setStoreData((previous) => ({
                        ...previous,
                        InvoiceEnable: event.target.checked,
                      }))
                    }
                  />
                }
                label="啟用線上發票"
              />
              <Stack spacing={1} sx={{ my: 1 }}>
                <Typography>捐贈發票愛心碼</Typography>
                {loveCodeRows.map((row, index) => (
                  <Stack
                    key={row.id}
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    alignItems="flex-start"
                  >
                    <TextField
                      fullWidth
                      label="受贈單位名稱"
                      color="secondary"
                      value={row.name}
                      required={!loveCodeErrors[index].canKeepUnnamed}
                      inputProps={{ "aria-label": `受贈單位名稱 ${index + 1}` }}
                      onChange={(event) =>
                        updateLoveCodeRow(row.id, "name", event.target.value)
                      }
                      error={loveCodeErrors[index].name}
                      helperText={
                        loveCodeErrors[index].name
                          ? "請輸入受贈單位名稱"
                          : ""
                      }
                    />
                    <TextField
                      fullWidth
                      required
                      label="愛心碼"
                      color="secondary"
                      value={row.code}
                      inputProps={{
                        inputMode: "numeric",
                        "aria-label": `愛心碼 ${index + 1}`,
                      }}
                      onChange={(event) =>
                        updateLoveCodeRow(row.id, "code", event.target.value)
                      }
                      error={Boolean(loveCodeErrors[index].code)}
                      helperText={loveCodeErrors[index].code || "3 至 7 位數"}
                    />
                    <Button
                      type="button"
                      color="error"
                      aria-label={`刪除愛心碼 ${index + 1}`}
                      onClick={() =>
                        setLoveCodeRows((previous) =>
                          previous.filter((item) => item.id !== row.id)
                        )
                      }
                    >
                      刪除
                    </Button>
                  </Stack>
                ))}
                <Button
                  type="button"
                  color="secondary"
                  sx={{ alignSelf: "flex-start" }}
                  onClick={() => {
                    const id = nextLoveCodeId.current++;
                    setLoveCodeRows((previous) => [
                      ...previous,
                      { id, name: "", code: "", originalCode: null, originalName: "" },
                    ]);
                  }}
                >
                  新增愛心碼
                </Button>
              </Stack>
              <TextField
                margin="dense"
                name="Name"
                label="店名(限6個字)"
                required={true}
                color="secondary"
                variant="outlined"
                fullWidth
                value={storeData.Name || ""}
                onChange={handleChange}
              />
              <TextField
                id="city-select"
                select
                name="City"
                label="選擇城市"
                color="info"
                fullWidth
                value={storeData.City || ""}
                onChange={handleChange}
              >
                {CITIES.map((city, index) => (
                  <MenuItem key={index} value={city}>
                    {city}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                margin="dense"
                name="District"
                label="地區"
                color="secondary"
                variant="outlined"
                fullWidth
                value={storeData.District || ""}
                onChange={handleChange}
              />
              <TextField
                margin="dense"
                name="Address"
                label="地址"
                color="secondary"
                variant="outlined"
                required={true}
                fullWidth
                value={storeData.Address || ""}
                onChange={handleChange}
              />
              <TextField
                margin="dense"
                name="Phone"
                label="電話"
                color="secondary"
                required={true}
                variant="outlined"
                fullWidth
                value={storeData.Phone || ""}
                onChange={handleChange}
              />
              <TextField
                margin="dense"
                name="Latitude"
                label="緯度"
                color="secondary"
                variant="outlined"
                fullWidth
                value={storeData.Latitude || 0}
                onChange={handleChange}
              />
              <TextField
                margin="dense"
                name="Longitude"
                label="經度"
                color="secondary"
                variant="outlined"
                fullWidth
                value={storeData.Longitude || 0}
                onChange={handleChange}
              />
              <StoreStatusSelect />
              <FormControlLabel
                control={
                  <Checkbox
                    color="warning"
                    checked={Boolean(storeData.HideFromWeb)}
                    onChange={(event) =>
                      setStoreData((previous) => ({
                        ...previous,
                        HideFromWeb: event.target.checked,
                      }))
                    }
                  />
                }
                label="前台隱藏"
              />
              <IntervalMinutesSelect />
              <TextField
                margin="dense"
                name="Description"
                label="描述"
                color="secondary"
                variant="outlined"
                fullWidth
                value={storeData.Description || ""}
                onChange={handleChange}
              />
            </Box>
            <Box width={"50%"} flexDirection={"colunm"}>
              {generateTagToggles()}
              {generatePromotionFields()}
              {!isAdd ? null : (
                <TextField
                  margin="dense"
                  name="Tables"
                  label="桌數"
                  color="secondary"
                  variant="outlined"
                  fullWidth
                  value={storeData.Tables || 0}
                  onChange={handleChange}
                />
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            取消
          </Button>
          <Button
            type="submit"
            color="secondary"
            disabled={invoiceSettingsInvalid || saving}
          >
            確認
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddStoreDialog;
